import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { formatOrderStatus } from '../../utils/orderStatus';
import { useCart } from '../../context/CartContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { Navbar } from '../../components/Navbar';
import { PushSubscribeButton } from '../../components/PushSubscribeButton';
import { CustomerTierCard } from '../../components/CustomerTierCard';
import { usePoints } from '../../hooks/usePoints';
import ProductRatingBadge from '../../components/reviews/ProductRatingBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Package,
  ShoppingCart,
  User,
  MapPin,
  Clock,
  Plus,
  Minus,
  Leaf,
  Cookie,
  Droplets,
  Wrench,
  Heart,
  Search,
  RotateCcw,
  Sparkles,
  Gift,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { FLOWER_SIZES, DEFAULT_FLOWER_SIZE, flowerSizesForProduct, effectsForProduct, effectEmoji } from '../../utils/flowerSizes';
import {
  hasVariants,
  getVariants,
  variantPriceFor,
  variantStockFor,
} from '../../utils/productVariants';
import { StrainFinderModal } from '../../components/StrainFinderModal';
import { ProductDetailDrawer, FeaturedCard, MiniGridCard } from '../../components/ProductDetailDrawer';
import { ReviewLaunchBanner } from '../../components/reviews/ReviewLaunchBanner';
import { ThemeLaunchBanner } from '../../components/ThemeLaunchBanner';
import CustomerChatHeaderIcon from '../../components/CustomerChatHeaderIcon';
import { isSoldOut, soldOutLast } from '../../utils/customerStock';
import { SoldOutButton } from '../../components/SoldOutButton';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const categoryIcons = {
  flower: Leaf,
  preroll: Leaf,        // Phase 6.0 — preroll category (same leaf icon as flower)
  edibles: Cookie,
  concentrates: Droplets,
  merch: Wrench,        // Phase 5.9.7 — renamed from 'accessories'
  accessories: Wrench,  // back-compat for any in-flight data
};

// Phase 6.0 — compute the lowest-tier price a customer would see for a product,
// used to render "from $X" on the FULL MENU_ thumbnails. Falls back to the
// flat `product.price` for non-flower, non-variant products.
function computeFromPrice(product, region) {
  if (!product) return 0;
  if (product.category === 'flower') {
    const sizes = flowerSizesForProduct(product, region);
    if (sizes.length > 0) return Math.min(...sizes.map((s) => s.price));
  }
  if (hasVariants(product)) {
    const variants = getVariants(product);
    const prices = variants
      .map((v) => variantPriceFor(product, v.key, region))
      .filter((p) => typeof p === 'number' && p > 0);
    if (prices.length > 0) return Math.min(...prices);
  }
  return Number(product.price ?? 0);
}

const byDropRecency = (a, b) => String(b.featured_at || "").localeCompare(String(a.featured_at || ""));

export function CustomerDashboard() {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchEnabled = useFlag('customer.search', true);
  const favoritesEnabled = useFlag('customer.favorites', true);
  const reorderEnabled = useFlag('customer.reorder', true);
  const loyaltyEnabled = useFlag('customer.loyalty', true);
  // Feb 2026 — Customer Tier card is launch-gated. Flip
  // `customer.tier_visible` to true on the morning of the announcement email
  // so customers see the SILVER/GOLD/PLATINUM/ONYX card the same moment the
  // email lands. Operator tooling (TierConfigPanel, distribution widget,
  // daily brief) stays visible behind the operator role gate regardless.
  const tierVisibleEnabled = useFlag('customer.tier_visible', false);
  const { balance: pointsBalance, refresh: refreshPoints } = usePoints();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [strainFinderOpen, setStrainFinderOpen] = useState(false);
  // Phase 6.0 Sub-Phase 1b — product detail drawer state.
  const [openDetailId, setOpenDetailId] = useState(null);
  // Phase 5.9.20 — VIEW AS CUSTOMER preview-mode flag. Set when operator
  // (super_admin/dispatcher) lands here via `/customer?preview=1` so we can
  // surface a sticky banner to return to inventory and visually indicate
  // they're not in a real customer session.
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Feb 2026 — React Query (Phase 2). Products list is shared with OrdersPage
  // via the same `['customer', 'products']` key so hopping between them is
  // instant. Orders / favorites / tier get their own keys with the shared
  // 30s staleTime default. Auth-required calls are wrapped in per-query
  // error boundaries via `enabled` so anon operators previewing the page
  // don't spam 401s.
  const productsQuery = useQuery({
    queryKey: ['customer', 'products'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/products`);
      return data || [];
    },
  });
  const authed = !!(user && user.id);
  const ordersQuery = useQuery({
    queryKey: ['customer', 'orders'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      return data || [];
    },
    enabled: authed,
  });
  const favoritesQuery = useQuery({
    queryKey: ['customer', 'favorites'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/favorites`, { withCredentials: true });
      return data?.favorites || [];
    },
    enabled: authed,
  });
  const tierQuery = useQuery({
    queryKey: ['customer', 'tier'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/users/me/tier`, { withCredentials: true });
      return data || null;
    },
    enabled: authed,
  });
  const products = productsQuery.data || [];
  const orders = ordersQuery.data || [];
  const favorites = favoritesQuery.data || [];
  const tierData = tierQuery.data || null;
  // Only show the skeleton on the true cold-start (no cache); back-nav uses
  // the cache and refetches silently in the background.
  const loading = productsQuery.isLoading;

  // Phase 6.0 — deep-link support: if URL has ?open={productId}, auto-open
  // that product's detail drawer once products are loaded. Used by the SA
  // "VIEW AS CUSTOMER →" ghost link on the inventory page so the operator
  // can verify their edits look right without scrolling/searching.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (openId) setOpenDetailId(openId);
    if (params.get('preview') === '1') setIsPreviewMode(true);
  }, []);

  const fetchData = useCallback(async () => {
    // Feb 2026 — data fetching moved to useQuery hooks above. This helper is
    // retained as a shim so existing callers (post-checkout invalidation,
    // manual refresh buttons) can still request a fresh pass across all
    // four queries. Invalidating individually would be more surgical but
    // this keeps every existing call site working without changes.
    await Promise.all([
      productsQuery.refetch(),
      ordersQuery.refetch(),
      favoritesQuery.refetch(),
      tierQuery.refetch(),
    ]);
  }, [productsQuery, ordersQuery, favoritesQuery, tierQuery]);
  const filteredProducts = products.filter(p => {
    if (selectedCategory !== 'all' && p.category !== selectedCategory) return false;
    if (onlyFavorites && !favorites.includes(p.id)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const hay = [
        p.name,
        p.category,
        p.strain_type,
        p.description
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Phase 6.0 Sub-Phase 1b — featured products surface above the grid as
  // hero "Today's Drops" cards. Falls back to empty list (backward-compat
  // for catalogs not yet using the `featured` flag).
  // Phase 6.0 Sub-Phase 2 — use region-aware stock check so featured products
  // with per-region inventory (region_stock.dmv > 0, legacy stock=0) actually
  // surface to the right customers instead of being silently hidden.
  const featuredProducts = filteredProducts.filter(
    (p) => p.featured && !isSoldOut(p, user?.region),
  ).sort(byDropRecency);
  const featuredIds = new Set(featuredProducts.map((p) => p.id));
  const gridProducts = soldOutLast(filteredProducts.filter((p) => !featuredIds.has(p.id)), user?.region);
  const openProduct = openDetailId ? products.find((p) => p.id === openDetailId) : null;

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));

  // Phase 5.8 — smart compact greeting (replaces "WELCOME, USER_ // CUSTOMER TERMINAL").
  // Picks the highest-signal subtitle: out-for-delivery → processing → points momentum → fallback.
  const greeting = (() => {
    const hr = new Date().getHours();
    if (hr >= 5  && hr < 12) return { word: 'GM',  emoji: '☀️' };
    if (hr >= 12 && hr < 17) return { word: 'GA',  emoji: '🌤' };
    if (hr >= 17 && hr < 22) return { word: 'GE',  emoji: '🌙' };
    return { word: 'GN', emoji: '✨' };
  })();
  const firstName = (user?.name || 'FRIEND').split(' ')[0].toUpperCase();
  const subtitle = (() => {
    const outForDelivery = activeOrders.find(o => o.status === 'out_for_delivery');
    if (outForDelivery) {
      return { text: 'ORDER ON THE WAY', color: '#39FF14', pulse: true };
    }
    if (activeOrders.length > 0) {
      return {
        text: `${activeOrders.length} ORDER${activeOrders.length > 1 ? 'S' : ''} PROCESSING`,
        color: '#FF9F0A',
      };
    }
    if (loyaltyEnabled && pointsBalance > 0) {
      // 20 pts = $1 (loyalty.py default).
      const dollarsRedeemable = Math.floor(pointsBalance / 20);
      if (dollarsRedeemable >= 5) {
        return { text: `$${dollarsRedeemable} OFF READY TO REDEEM`, color: '#FF9F0A' };
      }
      const ptsToFive = 100 - (pointsBalance % 100);
      return { text: `${ptsToFive} PTS TO $5 OFF`, color: '#A3C7A8' };
    }
    return null;
  })();

  const toggleFavorite = useCallback(async (productId) => {
    // Optimistic update — Feb 2026: writes go straight to the react-query
    // cache so the heart icon flips immediately AND siblings (Featured
    // strip, MiniGridCards, drawer) reading the same key re-render.
    const FAV_KEY = ['customer', 'favorites'];
    queryClient.setQueryData(FAV_KEY, (prev = []) => (
      prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]
    ));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/users/me/favorites/${productId}`,
        {},
        { withCredentials: true }
      );
      queryClient.setQueryData(FAV_KEY, data.favorites || []);
    } catch (err) {
      // Revert on failure.
      queryClient.setQueryData(FAV_KEY, (prev = []) => (
        prev.includes(productId) ? prev.filter((x) => x !== productId) : [...prev, productId]
      ));
      toast.error('Could not update favorites');
    }
  }, [queryClient]);

  const reorderFromPastOrder = useCallback((order) => {
    // Match past-order product IDs to current products (must still exist + be in stock)
    const productsById = Object.fromEntries(products.map(p => [p.id, p]));
    let added = 0;
    let skipped = 0;
    order.items.forEach(item => {
      const p = productsById[item.product_id];
      if (!p || p.stock <= 0) {
        skipped += 1;
        return;
      }
      // Respect stock as a ceiling
      const qty = Math.min(item.quantity, p.stock);
      if (qty <= 0) {
        skipped += 1;
        return;
      }
      if (item.size) {
        // Phase 5.9.3/4 — pull per-product + per-region price override
        const sizes = flowerSizesForProduct(p, user?.region);
        const cfg = sizes.find((s) => s.key === item.size);
        if (cfg) {
          addToCart(p, qty, { size: cfg.key, eighths: cfg.eighths, unitPrice: cfg.price });
          added += 1;
          return;
        }
      }
      addToCart(p, qty);
      added += 1;
    });
    if (added === 0) {
      toast.error('None of those items are available right now');
      return;
    }
    if (skipped > 0) {
      toast.warning(`Added ${added} item${added > 1 ? 's' : ''} to cart; ${skipped} unavailable`);
    } else {
      toast.success(`Added ${added} item${added > 1 ? 's' : ''} to cart`);
    }
    navigate('/customer/cart');
  }, [products, addToCart, navigate]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[rgba(255,159,10,0.1)] text-[#FF9F0A] border-[#FF9F0A]',
      confirmed: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      preparing: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      out_for_delivery: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      delivered: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      cancelled: 'bg-[rgba(255,59,48,0.1)] text-[#FF3B30] border-[#FF3B30]'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-[#39FF14] font-mono">
            <span className="cursor-blink">█</span> LOADING_DATA...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="customer-dashboard">
      <Navbar />

      {/* Phase 5.9.20 — VIEW AS CUSTOMER preview banner. Sticky strip at the
          top tells the operator they're previewing (not in a real customer
          session) and gives a clear path back to inventory. Cookies + auth
          stay intact across the navigation since it's same-tab. */}
      {isPreviewMode && (
        <div
          className="sticky top-0 z-40 bg-[#FF9F0A] text-[#050003] border-b-2 border-[#050003] px-4 py-2 flex items-center justify-between gap-3"
          data-testid="customer-preview-banner"
        >
          <span className="font-mono text-[10px] sm:text-xs uppercase tracking-wider font-bold">
            ⚠ PREVIEW MODE — viewing as a customer (your operator session is intact)
          </span>
          <Link
            to="/dispatch/inventory"
            className="font-mono text-[10px] sm:text-xs uppercase tracking-wider font-bold underline shrink-0 hover:text-[#050003]/70"
            data-testid="customer-preview-return"
          >
            ← Return to Inventory
          </Link>
        </div>
      )}

      {/* V2 dashboard shelved & fully removed Feb 2026. All customers stay on V1. */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header — greeting stacks above icons on mobile, side-by-side on sm+ */}
        <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-[#39FF14] font-mono text-xl sm:text-2xl font-bold neon-text">
              {'>'} {greeting.word}, {firstName} <span aria-hidden="true">{greeting.emoji}</span>
            </h1>
            {subtitle ? (
              <p
                className={`font-mono text-[10px] sm:text-xs mt-1 uppercase tracking-wider ${subtitle.pulse ? 'animate-pulse' : ''}`}
                style={{ color: subtitle.color }}
                data-testid="customer-greeting-subtitle"
              >
                {'//'} {subtitle.text}
              </p>
            ) : (
              <p className="text-[#4B6B52] font-mono text-[10px] sm:text-xs mt-1">// READY TO RESTOCK?</p>
            )}
          </div>
          <div className="flex gap-2 items-center justify-end flex-wrap">
            {loyaltyEnabled && (
              <Link
                to="/customer/profile"
                className="px-2 sm:px-3 py-1.5 border border-[#FF9F0A] bg-[rgba(255,159,10,0.06)] hover:bg-[rgba(255,159,10,0.12)] font-mono text-xs uppercase text-[#FF9F0A] rounded-none flex items-center gap-1.5 transition-colors"
                data-testid="points-balance-pill"
                title="Your loyalty points — tap for full history"
              >
                <Sparkles className="w-3 h-3" />
                <span>{pointsBalance.toLocaleString()}</span>
                <span className="hidden sm:inline">PTS</span>
              </Link>
            )}
            <PushSubscribeButton testId="customer-push-toggle" iconOnlyOnMobile />
            <Link to="/customer/refer" data-testid="refer-friends-link" aria-label="Refer friends">
              <Button variant="outline" className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none px-2 sm:px-3" title="Refer friends">
                <Gift className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">REFER</span>
              </Button>
            </Link>
            <Link to="/customer/orders" data-testid="view-orders-link" aria-label="My orders">
              <Button variant="outline" className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none px-2 sm:px-3" title="My orders">
                <Package className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">MY ORDERS ({orders.length})</span>
                <span className="sm:hidden text-xs ml-1">{orders.length}</span>
              </Button>
            </Link>
            <CustomerChatHeaderIcon user={user} />
            <Link to="/customer/profile" data-testid="view-profile-link" aria-label="Profile">
              <Button variant="outline" className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none px-2 sm:px-3" title="Profile">
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">PROFILE</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Phase T1 — Customer Tier card (visibility). Shown to customers
            with loyalty enabled, pinned above the active orders alert so
            it's the first thing they see after the header.
            Feb 2026 — also gated on `customer.tier_visible` so we can hold
            the reveal until the announcement email goes out. */}
        {loyaltyEnabled && tierVisibleEnabled && tierData && (
          <div className="mb-6">
            <CustomerTierCard
              tier={tierData.effective_tier || tierData.tier || 'standard'}
              annualOrders={Number(tierData.annual_orders) || 0}
              annualPoints={Number(tierData.annual_points) || 0}
              toNextOrders={tierData.to_next_orders ?? null}
              toNextPoints={tierData.to_next_points ?? null}
              nextTier={tierData.next_tier ?? null}
              qualifyingPath={tierData.qualifying_path ?? null}
              pointsBalance={Number(tierData.points_balance ?? pointsBalance) || 0}
              isLocked={Boolean(tierData.locked_until)}
            />
          </div>
        )}

        {/* Active Orders Alert */}
        {activeOrders.length > 0 && (
          <div className="bg-[#0C0612] border border-[#39FF14] p-4 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#39FF14] status-pulse" />
                <span className="text-[#39FF14] font-mono">
                  {activeOrders.length} ACTIVE ORDER{activeOrders.length > 1 ? 'S' : ''}
                </span>
              </div>
              <Link to="/customer/orders">
                <Button className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none text-sm" data-testid="track-orders-btn">
                  TRACK NOW
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="shop" className="space-y-6">
          <TabsList className="bg-[#0C0612] border border-[#2A0F3D] p-1 rounded-none">
            <TabsTrigger 
              value="shop" 
              className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
              data-testid="tab-shop"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              SHOP
            </TabsTrigger>
            <TabsTrigger 
              value="orders" 
              className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#050003] text-[#A3C7A8] rounded-none font-mono"
              data-testid="tab-orders"
            >
              <Package className="w-4 h-4 mr-2" />
              RECENT ORDERS
            </TabsTrigger>
          </TabsList>

          <TabsContent value="shop" className="space-y-6">
            {/* Strain Finder banner — appears in shop view */}
            {(selectedCategory === 'all' || selectedCategory === 'flower') && (
              <button
                type="button"
                onClick={() => setStrainFinderOpen(true)}
                className="w-full text-left rounded-2xl p-4 border-2 transition-all flex items-center gap-4 group"
                style={{
                  borderColor: '#B400FF',
                  background: 'linear-gradient(135deg, rgba(180,0,255,0.08) 0%, rgba(255,176,0,0.05) 100%)',
                  boxShadow: '0 0 24px rgba(180,0,255,0.15)',
                }}
                data-testid="strain-finder-banner"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: 'rgba(180,0,255,0.18)' }}>
                  <Sparkles className="w-6 h-6" style={{ color: '#FFB000' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-base uppercase tracking-tight">Strain Finder</p>
                  <p className="text-[#A3C7A8] text-xs mt-0.5">Not sure what to pick? Get a personalized match in 5 seconds.</p>
                </div>
                <span className="text-[#B400FF] font-bold uppercase text-xs tracking-widest group-hover:translate-x-1 transition-transform">START →</span>
              </button>
            )}

            {/* Search + Favorites */}
            {(searchEnabled || favoritesEnabled) && (
              <div className="flex gap-3 flex-wrap items-center">
                {searchEnabled && (
                  <div className="relative flex-1 min-w-[200px] max-w-lg">
                    <Search className="w-4 h-4 text-[#4B6B52] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search products..."
                      className="pl-9 pr-9 bg-[#0C0612] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                      data-testid="product-search"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4B6B52] hover:text-[#FF3B30]"
                        data-testid="clear-search"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                {favoritesEnabled && (
                  <Button
                    variant="outline"
                    onClick={() => setOnlyFavorites(v => !v)}
                    className={`rounded-none font-mono text-sm ${
                      onlyFavorites
                        ? 'border-[#FF3B30] text-[#FF3B30] bg-[rgba(255,59,48,0.08)]'
                        : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#FF3B30] hover:text-[#FF3B30]'
                    }`}
                    data-testid="toggle-favorites-filter"
                    title="Show only favorites"
                  >
                    <Heart className={`w-4 h-4 mr-2 ${onlyFavorites ? 'fill-[#FF3B30]' : ''}`} />
                    FAVORITES ({favorites.length})
                  </Button>
                )}
              </div>
            )}

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('all')}
                className={`rounded-none font-mono text-sm ${
                  selectedCategory === 'all' 
                    ? 'bg-[#39FF14] text-[#050003]' 
                    : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]'
                }`}
                data-testid="filter-all"
              >
                ALL
              </Button>
              {['flower', 'preroll', 'edibles', 'concentrates', 'merch'].map(cat => {
                const Icon = categoryIcons[cat];
                return (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory(cat)}
                    className={`rounded-none font-mono text-sm ${
                      selectedCategory === cat 
                        ? 'bg-[#39FF14] text-[#050003]' 
                        : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]'
                    }`}
                    data-testid={`filter-${cat}`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {cat.toUpperCase()}
                  </Button>
                );
              })}
            </div>

            {/* Feb 2026 — Track B launch banner. Shown to customers with
                delivered products they haven't reviewed yet. Gated behind
                the `customer.reviews` flag internally, so this mount is
                unconditional. */}
            <ThemeLaunchBanner />
            <ReviewLaunchBanner onOpen={(pid) => setOpenDetailId(pid)} />

            {/* Featured / Today's Drops — Phase 6.0 Sub-Phase 1b.
                Only renders when at least one product has `featured: true`.
                Hidden when the user has narrowed by search/favorites/category
                away from any featured items (avoids an empty hero row). */}
            {featuredProducts.length > 0 && (
              <section data-testid="featured-section">
                <h2 className="text-[#39FF14] font-mono uppercase tracking-wider text-xs mb-3">
                  {'>'} THE DROP_
                </h2>
                <div className="space-y-3">
                  {featuredProducts.map((p) => (
                    <FeaturedCard
                      key={p.id}
                      product={p}
                      fromPrice={computeFromPrice(p, user?.region)}
                      onOpen={() => setOpenDetailId(p.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* FULL MENU_ — compact 2-column thumbnail grid (Phase 6.0
                follow-up; matches the approved prototype layout). Each card
                is just thumbnail + name + one-line tagline + from-price.
                Tap → opens the detail drawer for size/variants/ADD. */}
            {gridProducts.length > 0 && (
              <section data-testid="full-menu-section">
                <h2 className="text-[#39FF14] font-mono uppercase tracking-wider text-xs mb-3">
                  {'>'} FULL MENU_
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {gridProducts.map((p) => (
                    <MiniGridCard
                      key={p.id}
                      product={p}
                      fromPrice={computeFromPrice(p, user?.region)}
                      onOpen={() => setOpenDetailId(p.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#4B6B52] font-mono">// NO PRODUCTS FOUND</p>
                {(searchQuery || onlyFavorites) && (
                  <Button
                    variant="outline"
                    onClick={() => { setSearchQuery(''); setOnlyFavorites(false); }}
                    className="mt-4 border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono"
                  >
                    CLEAR FILTERS
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            {orders.slice(0, 5).map(order => (
              <div key={order.id} className="bg-[#0C0612] border border-[#2A0F3D] p-4 hover:border-[#39FF14] transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-[#39FF14] font-mono text-sm">ORDER #{order.id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-[#4B6B52] font-mono text-xs">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className={`${getStatusBadge(order.status)} border rounded-none font-mono text-xs uppercase`}>
                    {formatOrderStatus(order.status)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-[#A3C7A8] font-mono text-sm">
                    {order.items.length} item{order.items.length > 1 ? 's' : ''} • ${Number(order.total || 0).toFixed(2)}
                  </div>
                  <div className="flex items-center gap-2">
                    {reorderEnabled && ['delivered', 'cancelled'].includes(order.status) && (
                      <Button
                        size="sm"
                        onClick={() => reorderFromPastOrder(order)}
                        className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none text-xs font-bold"
                        data-testid={`reorder-${order.id}`}
                        title="Add all items from this order to cart"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        REORDER
                      </Button>
                    )}
                    <Link to={`/customer/orders/${order.id}`}>
                      <Button variant="outline" size="sm" className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] rounded-none text-xs" data-testid={`view-order-${order.id}`}>
                        VIEW DETAILS
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}

            {orders.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
                <p className="text-[#4B6B52] font-mono">// NO ORDERS YET</p>
                <p className="text-[#4B6B52] font-mono text-sm mt-2">Start shopping to place your first order</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Strain Finder FAB — bottom-right, fixed */}
      <button
        type="button"
        onClick={() => setStrainFinderOpen(true)}
        data-testid="strain-finder-fab"
        aria-label="Open Strain Finder"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
        style={{
          background: 'linear-gradient(135deg, #B400FF 0%, #FFB000 100%)',
          boxShadow: '0 8px 24px rgba(180,0,255,0.45)',
        }}
      >
        <Sparkles className="w-6 h-6 text-white" />
      </button>

      <StrainFinderModal open={strainFinderOpen} onClose={() => setStrainFinderOpen(false)} />

      {/* Phase 6.0 Sub-Phase 1b — Product Detail Drawer (replaces the
          legacy VibeView modal as the single product-detail surface). */}
      {openProduct && (
        <ProductDetailDrawer
          product={openProduct}
          onClose={() => setOpenDetailId(null)}
          onAddToCart={addToCart}
          region={user?.region}
          isFavorite={favorites.includes(openProduct.id)}
          onToggleFavorite={favoritesEnabled ? toggleFavorite : null}
        />
      )}
    </div>
  );
}

function ProductCard({ product, onAddToCart, isFavorite, onToggleFavorite, onOpenDetail }) {
  const { user } = useAuth();
  const isFlower = product.category === 'flower';
  const isVariantMode = !isFlower && hasVariants(product);
  const [quantity, setQuantity] = useState(1);
  // Phase 2 — pick the first in-stock variant by default; fall back to first.
  const variants = isVariantMode ? getVariants(product) : [];
  const firstAvailableVariant = isVariantMode
    ? (variants.find((v) => variantStockFor(product, v.key, user?.region) > 0) || variants[0])
    : null;
  const [selectedSize, setSelectedSize] = useState(
    isFlower
      ? DEFAULT_FLOWER_SIZE.key
      : (firstAvailableVariant ? firstAvailableVariant.key : null),
  );

  // Phase 5.9.3/4 — pull per-product + per-region flower prices; falls back to default ladder.
  const sizes = isFlower ? flowerSizesForProduct(product, user?.region) : FLOWER_SIZES;

  const sizeCfg = isFlower
    ? (sizes.find((s) => s.key === selectedSize) || sizes[0])
    : null;
  const variantPrice = isVariantMode && selectedSize
    ? variantPriceFor(product, selectedSize, user?.region)
    : null;
  const displayPrice = isFlower
    ? sizeCfg.price
    : (isVariantMode ? variantPrice : product.price);

  const handleAddToCart = () => {
    if (isFlower) {
      onAddToCart(product, quantity, {
        size: sizeCfg.key,
        eighths: sizeCfg.eighths,
        unitPrice: sizeCfg.price,
      });
    } else if (isVariantMode && selectedSize) {
      onAddToCart(product, quantity, {
        size: selectedSize,
        eighths: 1,
        unitPrice: variantPrice,
      });
    } else {
      onAddToCart(product, quantity);
    }
    setQuantity(1);
  };

  const isOutOfStock = isSoldOut(product, user?.region);
  const effects = isFlower ? effectsForProduct(product) : [];

  return (
    <div className="bg-[#0C0612] border border-[#2A0F3D] hover:border-[#39FF14] transition-colors group" data-testid={`product-card-${product.id}`}>
      {/* Product Image — tapping it opens the new ProductDetailDrawer
          (Phase 6.0 Sub-Phase 1b — replaces the legacy VibeView modal).
          Phase 5.9.7 — favorite heart inside is also a button (invalid HTML
          when nested), so we use a div with role="button" + keyboard handlers
          to keep a11y intact. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpenDetail?.()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetail?.();
          }
        }}
        className="aspect-square bg-[#050003] relative overflow-hidden w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
        data-testid={`open-detail-${product.id}`}
        aria-label={`View ${product.name} details`}
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {(() => {
              const Icon = categoryIcons[product.category] || Package;
              return <Icon className="w-16 h-16 text-[#2A0F3D]" />;
            })()}
          </div>
        )}
        {product.strain_type && (
          <Badge className="absolute top-2 right-2 bg-[rgba(57,255,20,0.9)] text-[#050003] border-0 rounded-none font-mono text-xs uppercase">
            {product.strain_type}
          </Badge>
        )}
        {/* Favorite heart button — top-left */}
        {onToggleFavorite && (
          <button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleFavorite(product.id); }}
            className={`absolute top-2 left-2 p-2 bg-[#050003]/80 border border-[#2A0F3D] hover:border-[#FF3B30] transition-colors ${
              isFavorite ? 'text-[#FF3B30]' : 'text-[#4B6B52] hover:text-[#FF3B30]'
            }`}
            data-testid={`fav-${product.id}`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-[#FF3B30]' : ''}`} />
          </button>
        )}
        {/* Subtle hover hint */}
        <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-[#050003]/80 border border-[#39FF14]/40 text-[#39FF14] font-mono text-[10px] uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
          VIEW DETAILS
        </span>
        {isOutOfStock && (
          <div className="absolute inset-0 bg-[#050003]/80 flex items-center justify-center">
            <span className="text-[#FF3B30] font-mono text-sm uppercase" data-testid={`sold-out-label-${product.id}`}>SOLD OUT</span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-[#39FF14] font-mono font-bold text-sm truncate">{product.name}</h3>
          <p className="text-[#4B6B52] font-mono text-xs uppercase">{product.category}</p>
          <div className="mt-1">
            <ProductRatingBadge
              stats={product.review_stats}
              theme="dark"
              testId={`legacy-pcard-rating-${product.id}`}
            />
          </div>
        </div>

        <p className="text-[#A3C7A8] font-mono text-xs line-clamp-2">
          {product.vibe_description || product.description}
        </p>

        {/* Effect chips (flower only). Auto-derived from strain_type until admin sets product.effects. */}
        {isFlower && effects.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid={`effects-${product.id}`}>
            {effects.map((e) => (
              <span
                key={e}
                className="inline-flex items-center gap-1 px-2 py-1 border border-[#2A0F3D] text-[#A3C7A8] font-mono text-[10px] uppercase tracking-wider"
              >
                <span aria-hidden="true">{effectEmoji(e)}</span>
                {e}
              </span>
            ))}
          </div>
        )}

        {/* THC/CBD line removed per design — potency numbers no longer displayed app-wide. */}

        {/* Phase 2 — variant pills for apparel + concentrates (similar to flower size pills). */}
        {isVariantMode && !isOutOfStock && (
          <div
            className={`grid gap-2 pt-1 ${variants.length > 4 ? 'grid-cols-3' : 'grid-cols-2'}`}
            data-testid={`variants-${product.id}`}
          >
            {variants.map((v) => {
              const vStock = variantStockFor(product, v.key, user?.region);
              const oos = vStock <= 0;
              const vPrice = variantPriceFor(product, v.key, user?.region);
              const active = v.key === selectedSize;
              return (
                <button
                  key={v.key}
                  type="button"
                  disabled={oos}
                  onClick={() => setSelectedSize(v.key)}
                  data-testid={`variant-${product.id}-${v.key}`}
                  className={`flex items-center justify-between px-2 py-1.5 font-mono text-xs transition-all border ${
                    oos
                      ? 'bg-[#050003] border-[#2A0F3D] text-[#4B6B52] line-through cursor-not-allowed opacity-50'
                      : active
                        ? 'bg-[rgba(57,255,20,0.08)] border-[#39FF14] text-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.25)]'
                        : 'bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]/50'
                  }`}
                  title={oos ? 'Out of stock in your area' : `${v.label} · $${vPrice}`}
                >
                  <span className="font-bold">{v.label}</span>
                  {/* Show price only for concentrates (variant.price set); apparel uses shared base price. */}
                  {v.price ? (
                    <span className={active ? 'text-[#39FF14]' : 'text-[#4B6B52]'}>${vPrice}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        {/* Flower size pills (2x2) */}
        {isFlower && !isOutOfStock && (
          <div className="grid grid-cols-2 gap-2 pt-1" data-testid={`sizes-${product.id}`}>
            {FLOWER_SIZES.map((s) => {
              // Phase 5.9.3 — use per-product price if dispatcher set one for this size
              const perProduct = sizes.find((x) => x.key === s.key);
              const price = perProduct ? perProduct.price : s.price;
              const active = s.key === selectedSize;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSelectedSize(s.key)}
                  data-testid={`size-${product.id}-${s.key}`}
                  className={`flex items-center justify-between px-3 py-2 font-mono text-sm transition-all border ${
                    active
                      ? 'bg-[rgba(57,255,20,0.08)] border-[#39FF14] text-[#39FF14] shadow-[0_0_8px_rgba(57,255,20,0.25)]'
                      : 'bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]/50'
                  }`}
                >
                  <span className="font-bold">{s.label}</span>
                  <span className={active ? 'text-[#39FF14]' : 'text-[#4B6B52]'}>${price}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Price + Add to Cart */}
        <div className="flex items-center justify-between pt-2 border-t border-[#2A0F3D]">
          {!isFlower && !isVariantMode && (
            <span className="text-[#39FF14] font-mono font-bold text-lg">${Number(product.price ?? 0).toFixed(2)}</span>
          )}

          {!isOutOfStock && !isFlower && !isVariantMode && (
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-[#2A0F3D]">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-1 text-[#A3C7A8] hover:text-[#39FF14] hover:bg-[#2A0F3D]"
                  data-testid={`decrease-qty-${product.id}`}
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="px-2 text-[#39FF14] font-mono text-sm">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="p-1 text-[#A3C7A8] hover:text-[#39FF14] hover:bg-[#2A0F3D]"
                  data-testid={`increase-qty-${product.id}`}
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <Button
                onClick={handleAddToCart}
                className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none p-2"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Flower / variant wide ADD button — shows currently selected price */}
          {!isOutOfStock && (isFlower || isVariantMode) && (
            <Button
              onClick={handleAddToCart}
              disabled={isVariantMode && (
                !selectedSize || variantStockFor(product, selectedSize, user?.region) <= 0
              )}
              className="w-full bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono font-bold tracking-wider uppercase disabled:bg-[#2A0F3D] disabled:text-[#4B6B52]"
              data-testid={`add-to-cart-${product.id}`}
            >
              ADD ${displayPrice} <Plus className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {isOutOfStock && <SoldOutButton product={product} testId={`sold-out-btn-${product.id}`} />}
        {(() => {
          if (isOutOfStock) return null;
          if (product.stock <= 5) {
            return (
              <p className="text-[#FF9F0A] font-mono text-xs">
                Low stock — order soon
              </p>
            );
          }
          return null;
        })()}
      </div>
      {/* Phase 6.0 — VibeViewModal removed: ProductDetailDrawer (rendered at
          the page root) is the new single detail surface. */}
    </div>
  );
}
